import { track } from "@/lib/analytics"
import { useContext, useEffect, useRef, useState } from "react"
import { AuthContext } from "@/providers/AuthProvider"
import { createShare, type ShareCreated } from "../api"
import type { TurnView } from "../store"
import { contextParts } from "./format"
import { Overlay } from "./Overlay"
import { cardText, drawShareCard } from "./shareCard"
import "./pro.css"

interface ShareDialogProps {
  threadId: string
  view: TurnView
  /** "Forward to client" opens with the client view on. */
  client?: boolean
  onClose: () => void
}

type Options = { client: boolean; showName: boolean; name: string; firm: string }
const keyOf = (o: Options) => JSON.stringify([o.client, o.showName && o.name.trim(), o.showName && o.firm.trim()])

/**
 * Share this answer (Share-v4): WhatsApp and email first, then the link and a PDF. The link is
 * a frozen, masked copy made on the first tap and remade only when the options change.
 */
export function ShareDialog({ threadId, view, client = false, onClose }: ShareDialogProps) {
  const auth = useContext(AuthContext)
  const me = auth?.session?.user?.displayName ?? ""
  const [opts, setOpts] = useState<Options>({ client, showName: !!me, name: me, firm: "" })
  const [said, setSaid] = useState("")
  const [working, setWorking] = useState(false)
  const [shown, setShown] = useState("")
  const made = useRef(new Map<string, ShareCreated>())
  // The preview card, drawn once per dialog; the share goes without it if drawing fails.
  const card = useRef<Promise<string> | null>(null)

  const link = async (): Promise<string> => {
    const key = keyOf(opts)
    let share = made.current.get(key)
    if (!share) {
      card.current ??= drawShareCard(cardText(view)).catch(() => "")
      const jpeg = await card.current
      share = await createShare(threadId, {
        ...(jpeg ? { card_jpeg: jpeg } : {}),
        turn_id: view.turnId.startsWith("local-") ? undefined : view.turnId,
        client_view: opts.client,
        ...(opts.showName && opts.name.trim() ? { name: opts.name.trim(), firm: opts.firm.trim() } : {}),
      })
      made.current.set(key, share)
    }
    const url = `${window.location.origin}${share.path}`
    setShown(url)
    return url
  }

  const head = view.head
  const matter = contextParts(head?.context).join(" · ")
  const line = [head?.title || view.question, head?.short].filter(Boolean).join(": ")
  const message = (url: string) => `${line}\n\nResearch on Nira, not legal advice: ${url}`

  const act = async (what: "whatsapp" | "email" | "copy" | "pdf") => {
    // Open the window inside the click, so a pop-up blocker lets it through; point it once the link exists.
    const win = what === "whatsapp" || what === "pdf" ? window.open("", "_blank") : null
    setWorking(true)
    setSaid("")
    let url: string
    try {
      url = await link()
    } catch {
      win?.close()
      setSaid("Couldn't make the link. Try again.")
      setWorking(false)
      return
    }
    track("answer_shared", { channel: what === "copy" ? "copy_link" : what })
    const go = (to: string) => (win ? (win.location.href = to) : window.open(to, "_blank"))
    if (what === "whatsapp") go(`https://wa.me/?text=${encodeURIComponent(message(url))}`)
    else if (what === "pdf") go(`${url}?print=1`)
    else if (what === "email") {
      window.location.href = `mailto:?subject=${encodeURIComponent(head?.title || "Research on Nira")}&body=${encodeURIComponent(message(url))}`
    } else {
      try {
        await navigator.clipboard.writeText(url)
        setSaid("Link copied.")
      } catch {
        setSaid("This browser would not copy it. Select the link above and copy it.")
      }
    }
    setWorking(false)
  }

  const set = (patch: Partial<Options>) => setOpts((o) => ({ ...o, ...patch }))

  return (
    <Overlay label="Share this answer" onClose={onClose} wide>
      <h2 className="nr-ov-h">{client ? "Forward to your client" : "Share this answer"}</h2>
      <p className="nr-ov-p">
        GSTINs, PANs, phone numbers and emails are masked, and your uploaded documents stay out. Names in the text are
        not masked; check before you send. Anyone with the link can read it.
      </p>
      <div className="sh-go">
        <button type="button" className="sh-btn" disabled={working} onClick={() => void act("whatsapp")}>WhatsApp</button>
        <button type="button" className="sh-btn" disabled={working} onClick={() => void act("email")}>Email</button>
        <button type="button" className="sh-btn" disabled={working} onClick={() => void act("copy")}>Copy link</button>
        <button type="button" className="sh-btn" disabled={working} onClick={() => void act("pdf")}>PDF</button>
      </div>
      {shown ? (
        <input className="sh-url" readOnly value={shown} aria-label="Share link" onFocus={(e) => e.currentTarget.select()} />
      ) : null}
      {said ? <p className="nr-ov-said" role="status">{said}</p> : null}
      <div className="sh-opts">
        <label className="sh-opt">
          <span>Open on the client view<small>Plain words, rupees and dates, for the person who got the notice.</small></span>
          <input type="checkbox" role="switch" checked={opts.client} onChange={(e) => set({ client: e.target.checked })} />
        </label>
        <label className="sh-opt">
          <span>Show my name</span>
          <input type="checkbox" role="switch" checked={opts.showName} onChange={(e) => set({ showName: e.target.checked })} />
        </label>
        {opts.showName ? (
          <div className="sh-name">
            <label><span className="nr-ov-lb">Your name</span><input value={opts.name} maxLength={80} autoComplete="name" placeholder="R. Venkatesh" onChange={(e) => set({ name: e.target.value })} /></label>
            <label><span className="nr-ov-lb">Firm <span className="sh-optional">optional</span></span><input value={opts.firm} maxLength={120} autoComplete="organization" placeholder="Venkatesh & Co, Chartered Accountants" onChange={(e) => set({ firm: e.target.value })} /></label>
          </div>
        ) : null}
      </div>
      <div className="sh-card" aria-hidden="true">
        {matter ? <p className="sh-card-m">{matter}</p> : null}
        <p className="sh-card-h">{head?.short || view.question}</p>
        <p className="sh-card-f"><span>nira</span><span>research, not legal advice</span></p>
      </div>
      <button type="button" className="nr-ov-not" onClick={onClose}>Close</button>
    </Overlay>
  )
}

/**
 * Forward to client, in one tap (handoff 3 §12, NA-Client): the client-view link is made as the
 * card opens, with your name on it when you are signed in, and the card is ready to send. The
 * phone's share sheet where there is one, else WhatsApp; and copy. Every option is one tap further.
 */
export function ClientCard({ threadId, view, onClose, onMore }: { threadId: string; view: TurnView; onClose: () => void; onMore: () => void }) {
  const auth = useContext(AuthContext)
  const me = auth?.session?.user?.displayName ?? ""
  const [url, setUrl] = useState("")
  const [said, setSaid] = useState("")
  const started = useRef(false)
  const head = view.head
  const matter = contextParts(head?.context).join(" · ")
  const text = [head?.title || view.question, head?.short].filter(Boolean).join(": ")
  const message = (to: string) => `${text}\n\nResearch on Nira, not legal advice: ${to}`

  useEffect(() => {
    if (started.current) return
    started.current = true
    void (async () => {
      try {
        const jpeg = await drawShareCard(cardText(view)).catch(() => "")
        const share = await createShare(threadId, {
          ...(jpeg ? { card_jpeg: jpeg } : {}),
          turn_id: view.turnId.startsWith("local-") ? undefined : view.turnId,
          client_view: true,
          ...(me ? { name: me } : {}),
        })
        setUrl(`${window.location.origin}${share.path}`)
      } catch {
        setSaid("Couldn't make the link. Try again.")
      }
    })()
  }, [threadId, view, me])

  const send = async () => {
    if (!url) return
    const nav = navigator as Navigator & { share?: (d: ShareData) => Promise<void> }
    if (nav.share) {
      try {
        await nav.share({ title: head?.title || "Research on Nira", text: `${text}\n\nResearch on Nira, not legal advice.`, url })
        track("answer_shared", { channel: "native" })
        return
      } catch (err) {
        if ((err as { name?: string })?.name === "AbortError") return
      }
    }
    track("answer_shared", { channel: "whatsapp" })
    window.open(`https://wa.me/?text=${encodeURIComponent(message(url))}`, "_blank")
  }
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message(url))
      track("answer_shared", { channel: "copy_link" })
      setSaid("Copied, with the link.")
    } catch {
      setSaid("This browser would not copy it. Select the link and copy it.")
    }
  }

  return (
    <Overlay label="Forward to your client" onClose={onClose}>
      <h2 className="nr-ov-h">Forward to your client</h2>
      <div className="sh-card sh-client" aria-label="The card your client gets">
        {matter ? <p className="sh-card-m">{matter}</p> : null}
        <p className="sh-card-h">{head?.short || view.question}</p>
        <p className="sh-card-f"><span>nira</span><span>{me ? `from ${me} · ` : ""}research, not legal advice</span></p>
      </div>
      <p className="nr-ov-p">It opens on the client view: plain words, rupees and dates. GSTINs, PANs, phone numbers and emails are masked, and your documents stay out.</p>
      <div className="sh-go">
        <button type="button" className="sh-btn primary" disabled={!url} onClick={() => void send()}>{url ? "Send" : "Making the link…"}</button>
        <button type="button" className="sh-btn" disabled={!url} onClick={() => void copy()}>Copy</button>
      </div>
      {said ? <p className="nr-ov-said" role="status">{said}</p> : null}
      <button type="button" className="pro-link sh-more" onClick={onMore}>More options</button>
      <button type="button" className="nr-ov-not" onClick={onClose}>Close</button>
    </Overlay>
  )
}
